import React from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { DistributionResponse } from "../../api/admin/dashboard";

ChartJS.register(ArcElement, Tooltip, Legend);

interface DistributionChartProps {
  data: DistributionResponse | null;
  title: string;
  loading?: boolean;
  error?: string;
  labelKey: "species" | "region" | "city";
}

const DistributionChart: React.FC<DistributionChartProps> = ({
  data,
  title,
  loading,
  error,
  labelKey,
}) => {
  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
        <p className="mt-2 text-muted">Carregando {title.toLowerCase()}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!data || data.top.length === 0) {
    return (
      <div className="alert alert-info" role="alert">
        Nenhum dado disponível para {title.toLowerCase()}
      </div>
    );
  }

  // Preparar dados para o gráfico
  const labels = [
    ...data.top.map((item) => item[labelKey] || "Não informado"),
    ...(data.others > 0 ? ["Outros"] : []),
  ];

  const values = [
    ...data.top.map((item) => item.count),
    ...(data.others > 0 ? [data.others] : []),
  ];

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: [
          "#1e3a8a",
          "#2563eb",
          "#60a5fa",
          "#93c5fd",
          "#bfdbfe",
          "#64748b",
        ],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = data.total;
            const percentage = (total > 0 ? (value / total) * 100 : 0).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div>
      <h5 className="text-center mb-3">{title}</h5>
      <div style={{ height: "300px", position: "relative" }}>
        <Pie data={chartData} options={options} />
      </div>
      <div className="text-center mt-3">
        <small className="text-muted">
          Total: <strong>{data.total}</strong> colaborações
        </small>
      </div>
    </div>
  );
};

export default DistributionChart;
